'use client';

import { useState, useCallback, useRef, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Autocomplete from '@mui/material/Autocomplete';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Link from '@mui/material/Link';
import { Icon } from '@iconify/react';
import { useAuth, usePermissions } from '@/infrastructure/auth';
import { useToast } from '@/infrastructure/hooks';
import { useNavigationHistory, useApiRequestCapture, useConsoleLogCapture } from '@/infrastructure/bug-report';
import { useAreas } from '../application/hooks/useAreas';
import { useIterations } from '../application/hooks/useIterations';
import { useTeamMembers } from '../application/hooks/useTeamMembers';
import { useCreateBugReport } from '../application/hooks/useCreateBugReport';
import { useBugReportDrafts } from '../application/hooks/useBugReportDrafts';
import { buildContextPayload, buildFormData } from '../application/build-context-payload';
import { fixWebmDuration } from '../application/fix-webm-duration';
import { ImageGallery } from './ImageGallery';
import { MarkdownToolbar } from '@/features/i18n/translations/components/MarkdownToolbar';
import { isAcceptedAttachment, isAcceptedImage } from '@/features/bug-report/utils/file-acceptance';
import { materializeFiles } from '@/features/bug-report/utils/materialize-files';
import { BUG_PRIORITY_OPTIONS, DEFAULT_BUG_PRIORITY } from '@/features/bug-report/constants';
import type { TeamMember } from '../types';
import type { MediaCaptureEntry } from '@/infrastructure/bug-report/types';

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

/**
 * Module-level in-memory cache for non-serializable attachments (`File`
 * objects, recorded videos, capture metadata). The drafts store on
 * localStorage cannot hold these — but within a single browser session we
 * keep them here so closing and reopening the dialog (or switching between
 * the in-flight draft and a persisted one) does not lose user work.
 *
 * Keyed by draft uuid; in-flight drafts that have not yet been autosaved
 * use the SESSION_DRAFT_KEY sentinel until autosave assigns a uuid, at
 * which point the entry is migrated under the new key.
 */
interface AttachmentCacheEntry {
  images: File[];
  files: File[];
  hasVideo: boolean;
  mediaCaptures: MediaCaptureEntry[];
}

const SESSION_DRAFT_KEY = '__session__';
const attachmentCache = new Map<string, AttachmentCacheEntry>();

function getCachedAttachments(key: string | null): AttachmentCacheEntry | undefined {
  return attachmentCache.get(key ?? SESSION_DRAFT_KEY);
}

function setCachedAttachments(key: string | null, entry: AttachmentCacheEntry): void {
  const cacheKey = key ?? SESSION_DRAFT_KEY;
  if (entry.images.length === 0 && entry.files.length === 0 && entry.mediaCaptures.length === 0) {
    attachmentCache.delete(cacheKey);
  } else {
    attachmentCache.set(cacheKey, entry);
  }
}

function deleteCachedAttachments(key: string | null): void {
  attachmentCache.delete(key ?? SESSION_DRAFT_KEY);
}

function migrateCachedAttachments(fromKey: string | null, toKey: string | null): void {
  const fromCacheKey = fromKey ?? SESSION_DRAFT_KEY;
  const toCacheKey = toKey ?? SESSION_DRAFT_KEY;
  if (fromCacheKey === toCacheKey) return;
  const entry = attachmentCache.get(fromCacheKey);
  if (!entry) return;
  attachmentCache.delete(fromCacheKey);
  attachmentCache.set(toCacheKey, entry);
}

interface BugReportDialogProps {
  open: boolean;
  onClose: () => void;
  /**
   * If provided, the dialog opens pre-populated with the persisted draft of
   * this UUID. The dialog continues to autosave under the same UUID, so a
   * user resuming a draft does not duplicate it.
   */
  initialDraftUuid?: string | null;
}

const REASON_OPTIONS = [
  { value: 'New', label: 'New (Feature Request)' },
  { value: 'Build Failure', label: 'Build Failure (Bug)' },
];

export function BugReportDialog({ open, onClose, initialDraftUuid = null }: BugReportDialogProps) {
  const { user, roleCode, roleName, tenantUuid, tenantName } = useAuth();
  const { getNavigationHistory } = useNavigationHistory();
  const { getApiRequests } = useApiRequestCapture();
  const { getConsoleLogs } = useConsoleLogCapture();
  const { data: areas, isLoading: areasLoading } = useAreas();
  const {
    data: iterations,
    isLoading: iterationsLoading,
    isError: iterationsError,
  } = useIterations();
  const { hasPermission } = usePermissions();
  // Pre-assigning at creation mirrors the detail-page picker gating: same
  // permission as the assign mutation (PATCH /api/bugs/[id]) and the
  // team-members endpoint. Reporters without it just create unassigned bugs.
  const canAssign = hasPermission('platform-admin.bugs:access');
  const {
    data: teamMembers,
    isLoading: membersLoading,
    isError: membersError,
  } = useTeamMembers(canAssign && open);
  const createMutation = useCreateBugReport();
  const { upsertDraft, getDraft, deleteDraft, isDraftEmpty } = useBugReportDrafts();

  // Story 17.2.1: tracks the draft uuid this dialog session is autosaving under.
  // Set when the user types into an empty form, or pre-loaded when resuming.
  const [draftUuid, setDraftUuid] = useState<string | null>(initialDraftUuid);
  // The dialog mounts (with `keepMounted`) before `open` flips to true on
  // resume. This flag delays restoration until after the open transition so
  // the restore does not race the close-cleanup effect below.
  const restoredFromDraftRef = useRef<string | null>(null);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [reason, setReason] = useState('New');
  const [areaPath, setAreaPath] = useState('');
  const [priority, setPriority] = useState<number>(DEFAULT_BUG_PRIORITY);
  // Session-only (not part of the persisted draft): an optional assignee is
  // cheap to re-pick, and drafts would need the team list to rehydrate it.
  const [assignee, setAssignee] = useState<TeamMember | null>(null);
  const [iterationPath, setIterationPath] = useState('');
  const [additionalImages, setAdditionalImages] = useState<File[]>(
    () => getCachedAttachments(initialDraftUuid)?.images ?? [],
  );
  const [attachedFiles, setAttachedFiles] = useState<File[]>(
    () => getCachedAttachments(initialDraftUuid)?.files ?? [],
  );
  const toast = useToast();
  const [capturing, setCapturing] = useState(false);
  const [captureCount, setCaptureCount] = useState(0);
  const [showCaptureReview, setShowCaptureReview] = useState(false);
  const [barHidden, setBarHidden] = useState(false);
  const [recording, setRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [hasVideo, setHasVideo] = useState(
    () => getCachedAttachments(initialDraftUuid)?.hasVideo ?? false,
  );
  const [replaceVideoConfirmOpen, setReplaceVideoConfirmOpen] = useState(false);
  const [micEnabled, setMicEnabled] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem('bug-report:micEnabled') === 'true';
  });
  const streamRef = useRef<MediaStream | null>(null);
  const micStreamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const recordingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const recordedChunksRef = useRef<Blob[]>([]);
  const recordingStartedAtRef = useRef<string | null>(null);
  const mediaCapturesRef = useRef<MediaCaptureEntry[]>(
    getCachedAttachments(initialDraftUuid)?.mediaCaptures.slice() ?? [],
  );
  const descriptionRef = useRef<HTMLTextAreaElement>(null);

  const toggleMic = useCallback(() => {
    setMicEnabled((prev) => {
      const next = !prev;
      if (typeof window !== 'undefined') {
        window.localStorage.setItem('bug-report:micEnabled', String(next));
      }
      return next;
    });
  }, []);

  const teardownCapture = useCallback(() => {
    if (recorderRef.current?.state === 'recording') {
      recorderRef.current.stop();
    }
    recorderRef.current = null;
    setRecording(false);
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    setRecordingTime(0);
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    micStreamRef.current?.getTracks().forEach((t) => t.stop());
    micStreamRef.current = null;
    setCapturing(false);
  }, []);

  useEffect(() => {
    return () => { teardownCapture(); };
  }, [teardownCapture]);

  // Story 17.2.1: when closing, clear *recording-only* transient state
  // (live capture counters, in-flight recording metadata, mutation status).
  // Text fields stay because autosave has persisted them; attachments stay
  // in component state because the module-level attachmentCache holds the
  // File objects across close/reopen within the session — only reset to
  // defaults when both text and attachments are empty so reopening with a
  // fresh draft doesn't show stale data.
  const { reset } = createMutation;
  useEffect(() => {
    if (!open && !capturing) {
      setCaptureCount(0);
      recordingStartedAtRef.current = null;
      createMutation.reset();

      const textEmpty = isDraftEmpty({ title, description, reason, areaPath, priority });
      const attachmentsEmpty =
        additionalImages.length === 0 &&
        attachedFiles.length === 0 &&
        mediaCapturesRef.current.length === 0;
      if (textEmpty && attachmentsEmpty) {
        setTitle('');
        setDescription('');
        setReason('New');
        setAreaPath('');
        setPriority(DEFAULT_BUG_PRIORITY);
        setAssignee(null);
        setIterationPath('');
        setHasVideo(false);
        setDraftUuid(null);
        restoredFromDraftRef.current = null;
      }

      // Clear refs to avoid stale data in subsequent reports
      recordedChunksRef.current = [];
      recordingStartedAtRef.current = null;
      mediaCapturesRef.current = [];

      reset();
    }
  }, [open, capturing, reset]);

  // Restore from a persisted draft when the dialog opens with a draft uuid.
  // Text comes from localStorage; attachments (Files / MediaCaptures, which
  // can't be JSON-serialized) come from the module-level in-memory cache.
  useEffect(() => {
    if (!open) return;
    if (!initialDraftUuid) return;
    if (restoredFromDraftRef.current === initialDraftUuid) return;
    const draft = getDraft(initialDraftUuid);
    if (!draft) return;
    setTitle(draft.title);
    setDescription(draft.description);
    setReason(draft.reason || 'New');
    setAreaPath(draft.areaPath);
    setIterationPath(draft.iterationPath ?? '');
    setPriority(draft.priority ?? DEFAULT_BUG_PRIORITY);
    setDraftUuid(initialDraftUuid);
    const cached = getCachedAttachments(initialDraftUuid);
    if (cached) {
      setAdditionalImages(cached.images);
      setAttachedFiles(cached.files);
      setHasVideo(cached.hasVideo);
      mediaCapturesRef.current = cached.mediaCaptures.slice();
    } else {
      // Dialog is `keepMounted`, so the previous draft's attachment state
      // would otherwise leak into a draft that has no cached attachments.
      setAdditionalImages([]);
      setAttachedFiles([]);
      setHasVideo(false);
      mediaCapturesRef.current = [];
    }
    restoredFromDraftRef.current = initialDraftUuid;
  }, [open, initialDraftUuid, getDraft]);

  // Autosave field changes to localStorage. Debounced so we don't serialize
  // the full drafts map and broadcast a refresh event on every keystroke —
  // that was making the description field feel laggy for long inputs.
  // Skipped while the create mutation is in flight so a successful submit
  // can clear the draft without a race.
  useEffect(() => {
    if (!open) return;
    if (createMutation.isPending) return;
    const timer = setTimeout(() => {
      const next = upsertDraft(draftUuid, { title, description, reason, areaPath, iterationPath, priority });
      if (next !== draftUuid) {
        setDraftUuid(next);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [open, title, description, reason, areaPath, iterationPath, priority, draftUuid, upsertDraft, createMutation.isPending]);

  // When autosave promotes the in-flight session entry to a real draft uuid,
  // move the cached attachments under the new key so they stay associated
  // with the same draft going forward.
  // Guard: only migrate the autosave-promotion case (null -> uuid). Migrating
  // on any uuid change would clobber draft B's cache when the user switches
  // from draft A to draft B.
  const prevDraftUuidRef = useRef<string | null>(initialDraftUuid);
  useEffect(() => {
    const prev = prevDraftUuidRef.current;
    if (prev === null && draftUuid !== null) {
      migrateCachedAttachments(prev, draftUuid);
    }
    if (prev !== draftUuid) {
      prevDraftUuidRef.current = draftUuid;
    }
  }, [draftUuid]);

  // Sync attachments to the in-memory cache so closing and reopening the
  // dialog (or switching between Create and a resumed draft) preserves the
  // selected files within this browser session.
  useEffect(() => {
    setCachedAttachments(draftUuid, {
      images: additionalImages,
      files: attachedFiles,
      hasVideo,
      mediaCaptures: mediaCapturesRef.current.slice(),
    });
  }, [draftUuid, additionalImages, attachedFiles, hasVideo]);

  // URLs are minted inside the effect (not useMemo) so each cleanup revokes
  // the same URLs that were created in its corresponding setup. With useMemo,
  // StrictMode's simulated remount and any real unmount/remount could revoke
  // URLs while the memo cache still held the now-dead strings.
  const [captureReviewUrls, setCaptureReviewUrls] = useState<string[]>([]);
  useEffect(() => {
    const urls = additionalImages.map((file) => URL.createObjectURL(file));
    setCaptureReviewUrls(urls);
    return () => { urls.forEach((url) => URL.revokeObjectURL(url)); };
  }, [additionalImages]);

  useEffect(() => {
    return () => { captureReviewUrls.forEach((url) => URL.revokeObjectURL(url)); };
  }, [captureReviewUrls]);

  const isFormValid = title.trim() && description.trim() && reason && areaPath && priority && !areasLoading;

  const handleSubmit = useCallback(async () => {
    if (!user) return;

    const context = buildContextPayload({
      tenant: {
        uuid: tenantUuid ?? '',
        name: tenantName ?? '',
      },
      user: {
        uuid: user.uuid ?? '',
        email: user.email ?? '',
        roleCode: roleCode ?? '',
        roleName: roleName ?? '',
      },
      navigationHistory: getNavigationHistory(),
      apiRequests: getApiRequests(),
      consoleLogs: getConsoleLogs(),
      mediaCaptures: [...mediaCapturesRef.current],
    });

    const formData = buildFormData(
      { title, description, reason, areaPath, priority, assignedTo: assignee?.email, iterationPath: iterationPath || undefined },
      context,
      null,
      additionalImages,
      attachedFiles
    );

    createMutation.mutate(formData, {
      onSuccess: (data) => {
        // Story 17.2.1: a successful submit clears the persisted draft so the
        // floating badge does not keep nagging the user about completed work.
        deleteCachedAttachments(draftUuid);
        if (draftUuid) {
          deleteDraft(draftUuid);
          setDraftUuid(null);
          restoredFromDraftRef.current = null;
        }
        // Clear text fields and attachments explicitly — the close-cleanup
        // effect would otherwise see them as non-empty and skip the reset.
        setTitle('');
        setDescription('');
        setReason('New');
        setAreaPath('');
        setPriority(DEFAULT_BUG_PRIORITY);
        setAssignee(null);
        setIterationPath('');
        setAdditionalImages([]);
        setAttachedFiles([]);
        setHasVideo(false);
        mediaCapturesRef.current = [];
        toast.success(
          <>
            Bug report created successfully!{' '}
            {data?.workItemUrl && (
              <Link href={data.workItemUrl} target="_blank" rel="noopener">
                View in Azure DevOps
              </Link>
            )}
          </>
        );
        onClose();
      },
    });
  }, [title, description, reason, areaPath, priority, assignee, iterationPath, additionalImages, attachedFiles, user, roleCode, roleName, tenantUuid, tenantName, getNavigationHistory, getApiRequests, getConsoleLogs, createMutation, onClose, draftUuid, deleteDraft, toast]);

  // Explicit "Clear draft" — discards the persisted draft, the cached
  // attachments, and resets all fields.
  const handleClearDraft = useCallback(() => {
    deleteCachedAttachments(draftUuid);
    if (draftUuid) {
      deleteDraft(draftUuid);
      setDraftUuid(null);
      restoredFromDraftRef.current = null;
    }
    setTitle('');
    setDescription('');
    setReason('New');
    setAreaPath('');
    setPriority(DEFAULT_BUG_PRIORITY);
    setAssignee(null);
    setIterationPath('');
    setAdditionalImages([]);
    setAttachedFiles([]);
    setHasVideo(false);
    mediaCapturesRef.current = [];
  }, [draftUuid, deleteDraft]);

  // Picked files are copied into memory right away (see materializeFiles):
  // WebKit otherwise keeps them as live disk references, and a file that is
  // moved or deleted before submit makes Safari send an unparseable empty
  // multipart body. Files that are already unreadable are dropped here with
  // a toast asking the user to re-attach them.
  const notifyUnreadableFiles = useCallback((unreadable: string[]) => {
    if (unreadable.length === 0) return;
    toast.error(
      `Could not read ${unreadable.join(', ')}. The file may have been moved or deleted — please re-attach it.`
    );
  }, [toast]);

  const handleAddImages = useCallback(async (files: FileList | null) => {
    if (!files) return;
    const accepted = Array.from(files).filter(
      (f) => isAcceptedImage(f) && f.size <= 5 * 1024 * 1024
    );
    const { materialized, unreadable } = await materializeFiles(accepted);
    notifyUnreadableFiles(unreadable);
    if (materialized.length === 0) return;
    setAdditionalImages((prev) => [...prev, ...materialized].slice(0, 4));
  }, [notifyUnreadableFiles]);

  const handleRemoveImage = useCallback((index: number) => {
    setAdditionalImages((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleAddFiles = useCallback(async (files: FileList | null) => {
    if (!files) return;
    const accepted = Array.from(files).filter(
      (f) => isAcceptedAttachment(f) && f.size <= 10 * 1024 * 1024
    );
    const { materialized, unreadable } = await materializeFiles(accepted);
    notifyUnreadableFiles(unreadable);
    if (materialized.length === 0) return;
    setAttachedFiles((prev) => [...prev, ...materialized]);
  }, [notifyUnreadableFiles]);

  const handleRemoveFile = useCallback((index: number) => {
    setAttachedFiles((prev) => {
      const removed = prev[index];
      if (removed && removed.type.startsWith('video/')) {
        setHasVideo(false);
        recordedChunksRef.current = [];
        mediaCapturesRef.current = mediaCapturesRef.current.filter((c) => c.kind !== 'video');
      }
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  // Start capture mode: hide dialog first, then request screen share
  const handleStartCapture = useCallback(async () => {
    if (!navigator.mediaDevices?.getDisplayMedia) return;

    // Hide dialog and wait for close animation to finish
    setCapturing(true);
    await new Promise((r) => setTimeout(r, 400));

    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: true,
        preferCurrentTab: true,
      } as DisplayMediaStreamOptions);
      streamRef.current = stream;
      setCaptureCount(0);

      stream.getVideoTracks()[0]?.addEventListener('ended', () => {
        teardownCapture();
      });
    } catch {
      setCapturing(false);
    }
  }, [teardownCapture]);

  // Grab a single frame from the active stream
  // Uses excludeCurrentTab to avoid capturing the floating bar
  const handleGrabFrame = useCallback(async () => {
    const stream = streamRef.current;
    const track = stream?.getVideoTracks()[0];
    if (!track) return;

    // Hide bar, wait for the captured stream to reflect the DOM change.
    // Two animation frames + a short timeout is enough for the compositor
    // to drop the bar from the screen-share track on every browser we target.
    setBarHidden(true);
    await new Promise<void>((resolve) =>
      requestAnimationFrame(() => requestAnimationFrame(() => resolve()))
    );
    await new Promise((r) => setTimeout(r, 120));

    try {
      // Use a hidden video element to get a clean frame
      const video = document.createElement('video');
      video.srcObject = stream;
      video.muted = true;
      await video.play();

      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(video, 0, 0);
        const blob = await new Promise<Blob | null>((resolve) =>
          canvas.toBlob((b) => resolve(b), 'image/jpeg', 0.92)
        );
        if (blob) {
          const filename = `capture-${Date.now()}.jpg`;
          const file = new File([blob], filename, { type: 'image/jpeg' });
          setAdditionalImages((prev) => [...prev, file].slice(0, 4));
          setCaptureCount((c) => c + 1);
          mediaCapturesRef.current.push({
            id: `media-${filename}`,
            kind: 'screenshot',
            filename,
            startedAt: new Date().toISOString(),
          });
        }
      }
      video.pause();
      video.srcObject = null;
    } catch {
      // Frame grab failed
    } finally {
      setBarHidden(false);
    }
  }, []);

  // Start video recording from active stream
  const handleStartRecording = useCallback(async () => {
    const displayStream = streamRef.current;
    if (!displayStream) return;

    let recordingStream: MediaStream = displayStream;
    if (micEnabled && navigator.mediaDevices?.getUserMedia) {
      try {
        const mic = await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: true, noiseSuppression: true },
        });
        micStreamRef.current = mic;
        recordingStream = new MediaStream([
          ...displayStream.getVideoTracks(),
          ...mic.getAudioTracks(),
        ]);
      } catch {
        // mic denied or unavailable — record video-only
      }
    }

    recordedChunksRef.current = [];
    const hasAudio = recordingStream.getAudioTracks().length > 0;
    const mimeType = hasAudio && MediaRecorder.isTypeSupported('video/webm;codecs=vp9,opus')
      ? 'video/webm;codecs=vp9,opus'
      : hasAudio && MediaRecorder.isTypeSupported('video/webm;codecs=vp8,opus')
        ? 'video/webm;codecs=vp8,opus'
        : MediaRecorder.isTypeSupported('video/webm;codecs=vp9')
          ? 'video/webm;codecs=vp9'
          : 'video/webm';
    const recorder = new MediaRecorder(recordingStream, {
      mimeType,
      videoBitsPerSecond: 500_000,
      ...(hasAudio ? { audioBitsPerSecond: 96_000 } : {}),
    });
    const startedAt = new Date();
    recordingStartedAtRef.current = startedAt.toISOString();

    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) recordedChunksRef.current.push(e.data);
    };

    recorder.onstop = async () => {
      const stoppedAt = new Date();
      const durationMs = stoppedAt.getTime() - startedAt.getTime();
      const rawBlob = new Blob(recordedChunksRef.current, { type: mimeType });
      const blob = await fixWebmDuration(rawBlob, durationMs);
      const filename = `recording-${Date.now()}.webm`;
      const file = new File([blob], filename, { type: mimeType });
      setAttachedFiles((prev) => [...prev, file]);
      setHasVideo(true);
      mediaCapturesRef.current.push({
        id: `media-${filename}`,
        kind: 'video',
        filename,
        startedAt: recordingStartedAtRef.current ?? startedAt.toISOString(),
        stoppedAt: stoppedAt.toISOString(),
        durationMs,
      });
      recordingStartedAtRef.current = null;
      recordedChunksRef.current = [];
      micStreamRef.current?.getTracks().forEach((t) => t.stop());
      micStreamRef.current = null;
      if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
      setRecordingTime(0);
    };

    recorder.start(1000);
    recorderRef.current = recorder;
    setRecording(true);
    setRecordingTime(0);

    // Auto-stop at 30 seconds
    let elapsed = 0;
    recordingTimerRef.current = setInterval(() => {
      elapsed += 1;
      setRecordingTime(elapsed);
      if (elapsed >= 30) {
        if (recorderRef.current?.state === 'recording') {
          recorderRef.current.stop();
        }
        recorderRef.current = null;
        setRecording(false);
        if (recordingTimerRef.current) {
          clearInterval(recordingTimerRef.current);
          recordingTimerRef.current = null;
        }
      }
    }, 1000);
  }, [micEnabled]);

  const handleRecordClick = useCallback(() => {
    if (hasVideo) {
      setReplaceVideoConfirmOpen(true);
      return;
    }
    handleStartRecording();
  }, [hasVideo, handleStartRecording]);

  const handleConfirmReplaceVideo = useCallback(() => {
    setAttachedFiles((prev) => prev.filter((f) => !f.type.startsWith('video/')));
    setHasVideo(false);
    recordedChunksRef.current = [];
    mediaCapturesRef.current = mediaCapturesRef.current.filter((c) => c.kind !== 'video');
    setReplaceVideoConfirmOpen(false);
    handleStartRecording();
  }, [handleStartRecording]);

  // Stop video recording
  const handleStopRecording = useCallback(() => {
    if (recorderRef.current?.state === 'recording') {
      recorderRef.current.stop();
    }
    recorderRef.current = null;
    setRecording(false);
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
  }, []);

  const handleEndCapture = useCallback(() => {
    teardownCapture();
  }, [teardownCapture]);

  return (
    <>
      <Dialog
        open={open && !capturing}
        onClose={createMutation.isPending ? undefined : onClose}
        maxWidth="md"
        fullWidth
        keepMounted
      >
        <DialogTitle>Report a Bug</DialogTitle>
        <DialogContent>
          {areasLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>Loading form data...</Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField
                label="Title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                fullWidth
                inputProps={{ maxLength: 255 }}
                disabled={createMutation.isPending}
              />
              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
                <MarkdownToolbar
                  textareaRef={descriptionRef}
                  onValueChange={setDescription}
                  disabled={createMutation.isPending}
                />
                <TextField
                  placeholder="Description (supports Markdown) *"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  inputRef={descriptionRef}
                  required
                  fullWidth
                  multiline
                  rows={4}
                  disabled={createMutation.isPending}
                  sx={{ '& .MuiOutlinedInput-notchedOutline': { border: 'none' } }}
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <TextField
                  select
                  label="Reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  required
                  sx={{ flex: 1, minWidth: 180 }}
                  disabled={createMutation.isPending}
                >
                  {REASON_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  label="Priority"
                  value={priority}
                  onChange={(e) => setPriority(Number(e.target.value))}
                  required
                  sx={{ flex: 1, minWidth: 160 }}
                  disabled={createMutation.isPending}
                >
                  {BUG_PRIORITY_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  label="Area"
                  value={areaPath}
                  onChange={(e) => setAreaPath(e.target.value)}
                  required
                  sx={{ flex: 1, minWidth: 180 }}
                  disabled={createMutation.isPending}
                >
                  {areas?.map((area) => (
                    <MenuItem key={area.path} value={area.path}>{area.name}</MenuItem>
                  ))}
                </TextField>
              </Box>
              {canAssign && (
                <Autocomplete<TeamMember>
                  options={teamMembers ?? []}
                  loading={membersLoading}
                  disabled={createMutation.isPending}
                  value={assignee}
                  onChange={(_, selected) => setAssignee(selected)}
                  getOptionLabel={(option) => option.displayName}
                  isOptionEqualToValue={(option, value) =>
                    option.email.toLowerCase() === value.email.toLowerCase()
                  }
                  renderOption={({ key: _key, ...optionProps }, option) => (
                    // `key` must precede the spread: with key after a spread the
                    // JSX transform falls back to a path where React 19 no longer
                    // picks it up, and every option renders keyless.
                    <Box
                      key={option.email}
                      component="li"
                      {...optionProps}
                      sx={{ display: 'flex', gap: 1, alignItems: 'center' }}
                    >
                      <Avatar sx={{ width: 24, height: 24, fontSize: 12 }}>
                        {option.displayName.charAt(0).toUpperCase()}
                      </Avatar>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" noWrap>
                          {option.displayName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {option.email}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Assign to (optional)"
                      placeholder="Unassigned"
                      error={membersError}
                      helperText={
                        membersError ? 'Failed to load team members' : undefined
                      }
                    />
                  )}
                  sx={{ maxWidth: { sm: 360 } }}
                />
              )}
              <TextField
                select
                label="Iteration (optional)"
                value={iterationPath}
                onChange={(e) => setIterationPath(e.target.value)}
                disabled={createMutation.isPending || iterationsLoading}
                error={iterationsError}
                helperText={iterationsError ? 'Failed to load iterations' : undefined}
                size="small"
                sx={{ maxWidth: { sm: 360 } }}
              >
                <MenuItem value="">None</MenuItem>
                {iterations?.map((iter) => (
                  <MenuItem key={iter.path} value={iter.path}>{iter.name}</MenuItem>
                ))}
              </TextField>
              <ImageGallery
                screenshot={null}
                additionalImages={additionalImages}
                attachedFiles={attachedFiles}
                onAddImages={handleAddImages}
                onAddFiles={handleAddFiles}
                onAddCapture={handleStartCapture}
                onRemoveImage={handleRemoveImage}
                onRemoveFile={handleRemoveFile}
                disabled={createMutation.isPending}
              />
              {/* Story 17.2.1 (AC5): attachments are kept in memory for the
                  current browser session, but text-only fields are the part
                  that survives a page reload. */}
              <Alert severity="info" icon={<Icon icon="mdi:information-outline" width={20} />}>
                Attachments are kept while you stay on this page. Reload the
                page and you will need to re-add screenshots and files.
              </Alert>
              {createMutation.isError && (
                <Alert severity="error">
                  {/* The BFF returns an actionable, already-sanitized `message`
                      (surfaced by api.ts as the Error message). */}
                  {createMutation.error?.message || 'Failed to submit bug report. Please try again.'}
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between' }}>
          <Button
            onClick={handleClearDraft}
            disabled={
              createMutation.isPending ||
              (isDraftEmpty({ title, description, reason, areaPath, priority }) &&
                additionalImages.length === 0 &&
                attachedFiles.length === 0)
            }
            color="warning"
            startIcon={<Icon icon="mdi:eraser" width={18} />}
          >
            Clear draft
          </Button>
          <Box>
            <Button onClick={onClose} disabled={createMutation.isPending}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={!isFormValid || createMutation.isPending}
              startIcon={createMutation.isPending ? <CircularProgress size={16} /> : undefined}
            >
              {createMutation.isPending ? 'Submitting...' : 'Submit'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
      {/* Floating capture bar */}
      {capturing && !barHidden && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 32,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            bgcolor: 'background.paper',
            border: '2px solid',
            borderColor: 'error.main',
            borderRadius: 3,
            px: 3,
            py: 1.5,
            boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          }}
        >
          <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: 'error.main', animation: 'pulse 1.5s infinite', '@keyframes pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.4 } } }} />
          <Typography
            variant="body2"
            onClick={() => captureCount > 0 && setShowCaptureReview(true)}
            sx={{ fontWeight: 600, cursor: captureCount > 0 ? 'pointer' : 'default', textDecoration: captureCount > 0 ? 'underline' : 'none' }}
          >
            {recording
              ? `${formatDuration(recordingTime)} / ${formatDuration(30)}`
              : `${captureCount} captured`}
          </Typography>
          {recording && (
            <Box sx={{ width: 80, height: 4, borderRadius: 2, bgcolor: 'action.disabledBackground', overflow: 'hidden' }}>
              <Box sx={{ width: `${(recordingTime / 30) * 100}%`, height: '100%', bgcolor: 'error.main', transition: 'width 1s linear' }} />
            </Box>
          )}
          <Button
            variant="contained"
            color="error"
            size="small"
            onClick={handleGrabFrame}
            disabled={recording}
            startIcon={<Icon icon="material-symbols:photo-camera" width={18} />}
          >
            Screenshot
          </Button>
          <IconButton
            size="small"
            onClick={toggleMic}
            disabled={recording}
            aria-label={micEnabled ? 'Disable microphone' : 'Enable microphone'}
            title={micEnabled ? 'Microphone on (will record audio)' : 'Microphone off (video only)'}
            sx={{
              border: '1px solid',
              borderColor: micEnabled ? 'error.main' : 'divider',
              bgcolor: micEnabled ? 'error.main' : 'transparent',
              color: micEnabled ? '#fff' : 'text.primary',
              '&:hover': { bgcolor: micEnabled ? 'error.dark' : 'action.hover' },
            }}
          >
            <Icon icon={micEnabled ? 'material-symbols:mic' : 'material-symbols:mic-off'} width={18} />
          </IconButton>
          {recording ? (
            <Button
              variant="contained"
              size="small"
              color="warning"
              onClick={handleStopRecording}
              startIcon={<Icon icon="material-symbols:stop" width={18} />}
            >
              Stop
            </Button>
          ) : (
            <Button
              variant="outlined"
              size="small"
              color="error"
              onClick={handleRecordClick}
              startIcon={<Icon icon={hasVideo ? 'material-symbols:refresh' : 'material-symbols:videocam'} width={18} />}
            >
              {hasVideo ? 'Re-record' : 'Record'}
            </Button>
          )}
          <Button
            variant="outlined"
            size="small"
            onClick={handleEndCapture}
            disabled={recording}
            startIcon={<Icon icon="material-symbols:check" width={18} />}
          >
            Done
          </Button>
        </Box>
      )}
      {/* Capture review modal */}
      <Dialog open={showCaptureReview} onClose={() => setShowCaptureReview(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Captured Images ({additionalImages.length})</DialogTitle>
        <DialogContent>
          {additionalImages.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>No captures yet</Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {additionalImages.map((file, index) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Box
                    component="img"
                    src={captureReviewUrls[index]}
                    alt={file.name}
                    sx={{ width: 100, height: 60, objectFit: 'cover', borderRadius: 0.5, flexShrink: 0 }}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2">{file.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{(file.size / 1024).toFixed(0)} KB</Typography>
                  </Box>
                  <IconButton size="small" color="error" onClick={() => { setAdditionalImages((prev) => prev.filter((_, i) => i !== index)); setCaptureCount((c) => Math.max(0, c - 1)); }}>
                    <Icon icon="material-symbols:delete" width={20} />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCaptureReview(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={replaceVideoConfirmOpen}
        onClose={() => setReplaceVideoConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Replace existing recording?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            There is already a recorded video attached. Starting a new recording will permanently remove the previous one.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReplaceVideoConfirmOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleConfirmReplaceVideo}>
            Replace and record
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
